import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Check, MessageCircle, DollarSign, Loader2 } from 'lucide-react';
import { useOnboardingRedirect } from '../hooks/useOnboardingRedirect';
import { dispatchProfileCompletedEvent } from '../hooks/useOnboardingRedirect';
import * as onboardingApi from '../services/onboardingApi';
import { useFlowProtection, setFlowData } from '../../hooks/useFlowProtection';

const BackgroundGrid = () => (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden bg-[#F8F9FB]">
        {/* Ellipse 1: Main Gradient Background */}
        <div
            className="absolute"
            style={{
                width: '2618.47px',
                height: '1939.34px',
                left: '-448.14px',
                top: '-919.31px',
                background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.8) 0%, rgba(116, 153, 191, 0.465385) 41.83%, rgba(237, 242, 247, 0) 100%)',
                transform: 'rotate(26.84deg)',
                transformOrigin: 'top left',
            }}
        />

        {/* Ellipse 2827: Blurred Overlay */}
        <div
            className="absolute hidden md:block"
            style={{
                width: '640px',
                height: '458px',
                left: '50%',
                top: '282px',
                transform: 'translateX(-50%) matrix(1, 0, 0, -1, 0, 0)',
                background: 'rgba(255, 255, 255, 0.4)',
                filter: 'blur(93.2px)',
                borderRadius: '53px',
            }}
        />

        {/* Grid Lines */}
        <div className="absolute inset-0 flex justify-center pointer-events-none">
            <div className="w-full h-full max-w-[1920px] relative">
                {/* Vertical Lines */}
                {[...Array(12)].map((_, i) => (
                    <div
                        key={`v-${i}`}
                        className="absolute top-0 bottom-0 border-l border-dashed border-[rgba(40,40,42,0.2)]"
                        style={{ left: `${105 + (i * 120)}px` }}
                    />
                ))}

                {/* Horizontal Lines */}
                {[121, 300, 449, 627, 700, 872, 913, 1031].map((top, i) => (
                    <div
                        key={`h-${i}`}
                        className="absolute left-0 right-0 border-t border-dashed border-[rgba(40,40,42,0.2)]"
                        style={{ top: `${top}px` }}
                    />
                ))}
            </div>
        </div>
    </div>
);

const Stepper = ({ currentStep }: { currentStep: string }) => {
    const steps = [
        { label: 'Upload your resume', id: 'upload' },
        { label: 'Resume Review & Edit', id: 'review' },
        { label: 'Career Goals & Aspirations', id: 'goals' }
    ];

    const getStatus = (stepId: string) => {
        if (currentStep === 'goals') {
            if (stepId === 'goals') return 'active';
            return 'completed';
        }
        return 'upcoming';
    };

    return (
        <div className="flex items-center justify-center w-full max-w-3xl mx-auto mb-12 relative z-10 px-4">
            {steps.map((step, idx) => {
                const status = getStatus(step.id);
                const isActive = status === 'active';
                const isCompleted = status === 'completed';

                return (
                    <div key={idx} className="flex flex-1 items-center last:flex-none">
                        <div className="flex flex-col items-center relative">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center border-[3px] z-10 transition-colors duration-300 ${
                                isActive || isCompleted
                                    ? 'bg-[#8F9FB8] border-[#CBD5E1]'
                                    : 'bg-white border-[#CBD5E1]'
                            }`}>
                                {isActive && <div className="w-2 h-2 bg-white rounded-full" />}
                                {isCompleted && <div className="w-2 h-2 bg-white rounded-full opacity-0" />}
                            </div>
                            <span className={`absolute top-8 text-xs sm:text-sm font-medium whitespace-nowrap transition-colors duration-300 ${
                                isActive ? 'text-[#015FA6] font-bold' : 'text-[#015FA6]/70'
                            }`}>
                                {step.label}
                            </span>
                        </div>
                        {idx !== steps.length - 1 && (
                            <div className={`flex-1 h-[2px] mx-2 transition-colors duration-300 ${
                                isCompleted ? 'bg-[#8F9FB8]' : 'bg-[#CBD5E1]'
                            }`} />
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const Checkbox = ({ label, checked, onChange }: { label: string, checked: boolean, onChange: () => void }) => (
    <label className="flex items-start gap-2 cursor-pointer group">
        <div className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center transition-colors ${
            checked ? 'bg-[#015FA6] border-[#015FA6]' : 'border-gray-300 bg-white group-hover:border-[#015FA6]'
        }`}>
            {checked && <Check size={12} className="text-white" strokeWidth={3} />}
        </div>
        <span className={`text-xs ${checked ? 'text-[#015FA6] font-medium' : 'text-[#015FA6]/70'}`}>
            {label}
        </span>
        <input type="checkbox" className="hidden" checked={checked} onChange={onChange} />
    </label>
);

const Tag = ({ text, onRemove }: { text: string; onRemove?: () => void }) => (
    <div className="inline-flex items-center px-2 py-1 rounded bg-[#DCE6F1] text-[#015FA6] text-xs font-medium group">
        {text}
        {onRemove && (
            <button
                onClick={onRemove}
                className="ml-1.5 text-[#015FA6]/50 hover:text-[#015FA6] transition-colors"
                aria-label={`Remove ${text}`}
            >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>
        )}
    </div>
);

const TagInputSection = ({
    items,
    onAdd,
    onRemove
}: {
    items: string[],
    onAdd: (item: string) => void,
    onRemove: (index: number) => void
}) => {
    const [isAdding, setIsAdding] = useState(false);
    const [newValue, setNewValue] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isAdding && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isAdding]);

    const handleAdd = useCallback(() => {
        const trimmedValue = newValue.trim();
        if (trimmedValue) {
            onAdd(trimmedValue);
            setNewValue('');
            setIsAdding(false);
        } else {
            // If value is empty, just close the input
            setIsAdding(false);
            setNewValue('');
        }
    }, [newValue, onAdd]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAdd();
        } else if (e.key === 'Escape') {
            setIsAdding(false);
            setNewValue('');
        }
    }, [handleAdd]);

    const handleBlur = useCallback(() => {
        // Only add if there's actual content
        const trimmedValue = newValue.trim();
        if (trimmedValue) {
            onAdd(trimmedValue);
        }
        setNewValue('');
        setIsAdding(false);
    }, [newValue, onAdd]);

    return (
        <div className="flex flex-wrap gap-2">
            {items.map((item, idx) => (
                <Tag key={idx} text={item} onRemove={() => onRemove(idx)} />
            ))}

            {isAdding ? (
                <input
                    ref={inputRef}
                    type="text"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    className="w-32 px-2 py-1.5 text-sm border border-[#015FA6] rounded focus:outline-none focus:ring-2 focus:ring-[#015FA6]/20 text-[#015FA6]"
                    placeholder="Type & Enter..."
                />
            ) : (
                <button
                    onClick={() => setIsAdding(true)}
                    className="inline-flex items-center px-3 py-1.5 rounded border border-dashed border-[#015FA6]/40 text-[#015FA6] text-sm hover:bg-[#015FA6]/5 transition-colors"
                >
                    <Plus size={14} className="mr-1" />
                    Add more
                </button>
            )}
        </div>
    );
};

const Card = React.memo(({ title, children }: { title: string, children: React.ReactNode }) => (
    <div className="bg-white rounded-2xl shadow-[0px_2px_15px_rgba(0,0,0,0.05)] border border-gray-100 flex flex-col h-full p-4">
        <h3 className="text-[#015FA6] font-manrope font-bold text-lg mb-4">{title}</h3>
        <div className="flex-1">
            {children}
        </div>
    </div>
));

const CareerGoals: React.FC = () => {
    const navigate = useNavigate();

    // Flow protection - requires coming from profile review or login redirect
    const { isValidAccess } = useFlowProtection({
        storageKey: 'fromProfileReview',
        allowIncompleteProfile: true,
        fallbackPath: '/candidate/dashboard'
    });

    // Redirect users who have already completed onboarding
    useOnboardingRedirect();

    const [cities, setCities] = useState<string[]>([]);
    const [workType, setWorkType] = useState('Hybrid');
    const [workingDays, setWorkingDays] = useState({
        standard: false,
        fullTime: false,
        partTime: false,
        shiftWork: false,
        freelance: false
    });
    const [compensationAmount, setCompensationAmount] = useState('');
    const [compensationPackages, setCompensationPackages] = useState({
        baseBonus: false,
        baseCommission: false,
        commissionOnly: false,
        pnl: false
    });
    const [jobTitles, setJobTitles] = useState<string[]>([]);
    const [companies, setCompanies] = useState<string[]>([]);
    const [hasMissingQuestions, setHasMissingQuestions] = useState(false);
    const [checkingQuestions, setCheckingQuestions] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        checkForMissingQuestions();
    }, []);

    const checkForMissingQuestions = async () => {
        try {
            setCheckingQuestions(true);
            const userDataString = localStorage.getItem('authUser');
            if (!userDataString) return;

            const userData = JSON.parse(userDataString);
            const userId = userData.id;

            if (!userId) return;

            // Using /me endpoint - no userId needed
            const response = await onboardingApi.getCurrentOnboardingStatus();
            const status = response.payload;

            const pending = status.missingQuestions.filter(q => q.status === 'pending');
            setHasMissingQuestions(pending.length > 0);
        } catch (error) {
            console.error('Failed to check missing questions:', error);
            setHasMissingQuestions(false);
        } finally {
            setCheckingQuestions(false);
        }
    };

    const savePreferencesAndComplete = async () => {
        try {
            setIsSaving(true);
            console.log('[CareerGoals] Saving preferences and marking profile complete');

            const userDataString = localStorage.getItem('authUser');
            if (!userDataString) {
                console.error('[CareerGoals] No user data found');
                return false;
            }

            const userData = JSON.parse(userDataString);
            const userId = userData.id;

            if (!userId) {
                console.error('[CareerGoals] No user ID found');
                return false;
            }

            // Build preferences object
            const preferences = {
                cities,
                workType,
                workingDays,
                compensationAmount,
                compensationPackages,
                jobTitles,
                companies
            };

            console.log('[CareerGoals] Saving preferences:', preferences);

            // Save preferences to backend using /me endpoint
            await onboardingApi.updateCurrentPreferences(preferences);
            console.log('[CareerGoals] Preferences saved successfully');

            // Mark profile as completed (only if no missing questions) using /me endpoint
            if (!hasMissingQuestions) {
                console.log('[CareerGoals] No missing questions, marking profile complete');
                await onboardingApi.markCurrentProfileCompleted();
                console.log('[CareerGoals] Profile marked as completed');

                // Update localStorage
                userData.profile_completed = true;
                userData.profileCompleted = true;
                localStorage.setItem('authUser', JSON.stringify(userData));

                // Dispatch event to notify other components
                dispatchProfileCompletedEvent();
            }

            return true;
        } catch (error) {
            console.error('[CareerGoals] Failed to save preferences:', error);
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const handleContinueToDashboard = async () => {
        const success = await savePreferencesAndComplete();
        if (success) {
            navigate('/candidate/dashboard');
        }
    };

    const handleContinueToChat = async () => {
        const success = await savePreferencesAndComplete();
        if (success) {
            // Set flow data for next page
            setFlowData('fromCareerGoals', { preferencesSet: true });
            navigate('/welcome/onboarding-chat');
        }
    };


    const handleAddCity = useCallback((item: string) => {
        setCities(prev => [...prev, item]);
    }, []);

    const handleRemoveCity = useCallback((index: number) => {
        setCities(prev => prev.filter((_, i) => i !== index));
    }, []);

    const handleAddJobTitle = useCallback((item: string) => {
        setJobTitles(prev => [...prev, item]);
    }, []);

    const handleRemoveJobTitle = useCallback((index: number) => {
        setJobTitles(prev => prev.filter((_, i) => i !== index));
    }, []);

    const handleAddCompany = useCallback((item: string) => {
        setCompanies(prev => [...prev, item]);
    }, []);

    const handleRemoveCompany = useCallback((index: number) => {
        setCompanies(prev => prev.filter((_, i) => i !== index));
    }, []);
    
    // Don't render until flow access is validated
    if (isValidAccess === null) {
        return null;
    }



    return (
        <div className="min-h-screen bg-[#F8F9FB] font-manrope relative flex flex-col overflow-hidden">
            <BackgroundGrid />

            {/* Header */}
            <header className="relative z-10 pt-8 pb-4 text-center">
                <div className="mb-6">
                    <img
                        src="/branding/logo_blue.svg"
                        alt="Hush"
                        style={{
                            width: '100px',
                            height: '46px',
                            objectFit: 'contain',
                            margin: '0 auto'
                        }}
                    />
                </div>
                <Stepper currentStep="goals" />
            </header>

            {/* Main Content */}
            <main className="relative z-10 flex-1 px-4 md:px-8 py-4 max-w-[1400px] mx-auto w-full flex flex-col">
                <div className="space-y-3 px-4 max-w-6xl mx-auto text-center mb-8 animate-fade-in">
                    <h1 className="text-[#015FA6] font-bold text-[24px] sm:text-[32px] md:text-[40px] lg:text-[48px] leading-[100%] tracking-tight mb-4">
                        Let's understand what you're looking for
                    </h1>
                    <p className="text-[#015FA6] font-extralight text-[16px] sm:text-[18px] md:text-[20px] lg:text-[24px] leading-[150%] max-w-4xl mx-auto">
                        Tell us about your next ideal role — we'll match you to the right opportunities.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-full pb-20 max-w-[1100px] mx-auto w-full">

                    {/* Column 1: Location Preferences */}
                    <Card title="Location Preferences">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-[#015FA6] mb-1.5">City</label>
                                <TagInputSection
                                    items={cities}
                                    onAdd={handleAddCity}
                                    onRemove={handleRemoveCity}
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-[#015FA6] mb-1.5">Work Type</label>
                                <div className="flex rounded-lg border border-[#015FA6]/20 overflow-hidden">
                                    {['On-site', 'Hybrid', 'Remote'].map((type) => (
                                        <button
                                            key={type}
                                            onClick={() => setWorkType(type)}
                                            className={`flex-1 py-2 text-sm font-medium transition-colors ${
                                                workType === type
                                                    ? 'bg-[#015FA6] text-white'
                                                    : 'bg-white text-[#015FA6] hover:bg-gray-50'
                                            }`}
                                        >
                                            {type}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-[#015FA6] mb-1.5">Working Days</label>
                                <div className="space-y-2">
                                    <Checkbox
                                        label="Standard Schedule (Mon-Fri)"
                                        checked={workingDays.standard}
                                        onChange={() => setWorkingDays(p => ({...p, standard: !p.standard}))}
                                    />
                                    <Checkbox
                                        label="Full-Time (35-40 hrs)"
                                        checked={workingDays.fullTime}
                                        onChange={() => setWorkingDays(p => ({...p, fullTime: !p.fullTime}))}
                                    />
                                    <Checkbox
                                        label="Part-Time (< 35 hrs)"
                                        checked={workingDays.partTime}
                                        onChange={() => setWorkingDays(p => ({...p, partTime: !p.partTime}))}
                                    />
                                    <Checkbox
                                        label="Shift Work"
                                        checked={workingDays.shiftWork}
                                        onChange={() => setWorkingDays(p => ({...p, shiftWork: !p.shiftWork}))}
                                    />
                                    <Checkbox
                                        label="Freelance"
                                        checked={workingDays.freelance}
                                        onChange={() => setWorkingDays(p => ({...p, freelance: !p.freelance}))}
                                    />
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Column 2: Compensation */}
                    <Card title="Compensation">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-[#015FA6] mb-1.5">Total Compensation</label>
                                <div className="relative">
                                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[#015FA6]/40">
                                        <DollarSign size={16} />
                                    </div>
                                    <input
                                        type="text"
                                        value={compensationAmount}
                                        onChange={(e) => setCompensationAmount(e.target.value)}
                                        placeholder="e.g. 80,000"
                                        className="w-full pl-8 pr-3 py-2.5 border border-[#015FA6]/20 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#015FA6]/20 focus:border-[#015FA6]"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-[#015FA6] mb-1.5">Compensation packages</label>
                                <div className="space-y-2">
                                    <Checkbox
                                        label="Base + Bonus"
                                        checked={compensationPackages.baseBonus}
                                        onChange={() => setCompensationPackages(p => ({...p, baseBonus: !p.baseBonus}))}
                                    />
                                    <Checkbox
                                        label="Base + Commission"
                                        checked={compensationPackages.baseCommission}
                                        onChange={() => setCompensationPackages(p => ({...p, baseCommission: !p.baseCommission}))}
                                    />
                                    <Checkbox
                                        label="Commission only"
                                        checked={compensationPackages.commissionOnly}
                                        onChange={() => setCompensationPackages(p => ({...p, commissionOnly: !p.commissionOnly}))}
                                    />
                                    <Checkbox
                                        label="% of PnL"
                                        checked={compensationPackages.pnl}
                                        onChange={() => setCompensationPackages(p => ({...p, pnl: !p.pnl}))}
                                    />
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Column 3: Desired Jobs */}
                    <Card title="Desired Jobs">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-[#015FA6] mb-1.5">Job Title</label>
                                <TagInputSection
                                    items={jobTitles}
                                    onAdd={handleAddJobTitle}
                                    onRemove={handleRemoveJobTitle}
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-[#015FA6] mb-1.5">Companies of Interest</label>
                                <TagInputSection
                                    items={companies}
                                    onAdd={handleAddCompany}
                                    onRemove={handleRemoveCompany}
                                />
                            </div>
                        </div>
                    </Card>

                </div>
            </main>

            <footer className="fixed bottom-0 left-0 right-0 z-20 py-6 px-4 bg-[#F8F9FB]/90 backdrop-blur-sm border-t border-gray-200">
                <div className="max-w-4xl mx-auto flex justify-between items-center gap-4">
                    <button
                        onClick={() => navigate('/welcome/profile-review')}
                        className="flex items-center gap-2 px-8 py-3 rounded-lg bg-[#9A9A9A] text-white font-medium hover:bg-gray-600 transition-colors shadow-lg shadow-gray-400/20"
                    >
                        Back
                    </button>
                    {checkingQuestions || isSaving ? (
                        <button
                            disabled
                            className="flex items-center gap-2 px-12 py-3 rounded-lg bg-[#015FA6]/50 text-white font-bold cursor-not-allowed shadow-lg shadow-blue-900/20"
                        >
                            <Loader2 size={18} className="animate-spin" />
                            {isSaving ? 'Saving...' : 'Loading...'}
                        </button>
                    ) : hasMissingQuestions ? (
                        <button
                            onClick={handleContinueToChat}
                            disabled={isSaving}
                            className="flex items-center gap-2 px-8 py-3 rounded-lg bg-[#015FA6] text-white font-bold hover:bg-[#014f8c] transition-colors shadow-lg shadow-blue-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <MessageCircle size={18} />
                            Chat with David
                        </button>
                    ) : (
                        <button
                            onClick={handleContinueToDashboard}
                            disabled={isSaving}
                            className="flex items-center gap-2 px-12 py-3 rounded-lg bg-[#015FA6] text-white font-bold hover:bg-[#014f8c] transition-colors shadow-lg shadow-blue-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Dashboard
                        </button>
                    )}
                </div>
            </footer>
        </div>
    );
};

export default CareerGoals;
